/**
 * astronomy.ts — Accurate astronomical calculations for the biodynamic calendar.
 *
 * Uses astronomy-engine (Jean Meeus algorithms) for:
 *   - Moon sidereal zodiac position (Lahiri ayanamsa)
 *   - Moon phase angle & illumination
 *   - Moon ascending / descending (by declination)
 *   - Lunar node events (SearchMoonNode / NextMoonNode)
 *   - Perigee & apogee events (SearchLunarApsis / NextLunarApsis)
 *   - Planetary positions in sidereal zodiac
 */

import {
  GeoVector,
  Ecliptic,
  Body,
  MoonPhase as AstroMoonPhase,
  SearchLunarApsis,
  NextLunarApsis,
  SearchMoonNode,
  NextMoonNode,
  SearchMoonQuarter,
  NextMoonQuarter,
  ApsisKind,
  NodeEventKind,
} from 'astronomy-engine';

import { ZODIAC_TO_DAY_TYPE } from '@/types/biodynamic';
import type { BiodynamicDayType, MoonPhase, ZodiacSign } from '@/types/biodynamic';

// ── Lahiri ayanamsa ───────────────────────────────────────────────────────────
// Converts tropical ecliptic longitude → sidereal (constellation-based) longitude
// as used by the Maria Thun biodynamic calendar.
const AYANAMSA_J2000 = 23.853;          // degrees at J2000.0
const AYANAMSA_RATE  = 50.3 / 3_600;   // degrees / year (precession)
const J2000_MS = new Date('2000-01-01T12:00:00Z').getTime();

function getAyanamsa(date: Date): number {
  const years = (date.getTime() - J2000_MS) / (365.25 * 86_400_000);
  return AYANAMSA_J2000 + AYANAMSA_RATE * years;
}

function tropicalToSidereal(lon: number, date: Date): number {
  return ((lon - getAyanamsa(date)) % 360 + 360) % 360;
}

// ── Zodiac helpers ────────────────────────────────────────────────────────────
const ZODIAC_SIGNS: ZodiacSign[] = [
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
];

export const ZODIAC_SYMBOLS: Record<ZodiacSign, string> = {
  aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
  leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
  sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓',
};

export const ZODIAC_ELEMENT: Record<ZodiacSign, 'fire' | 'earth' | 'air' | 'water'> = {
  aries: 'fire',  taurus: 'earth', gemini: 'air',   cancer: 'water',
  leo:   'fire',  virgo:  'earth', libra:  'air',   scorpio: 'water',
  sagittarius: 'fire', capricorn: 'earth', aquarius: 'air', pisces: 'water',
};

function lonToSign(lon: number): ZodiacSign {
  return ZODIAC_SIGNS[Math.floor(((lon % 360) + 360) % 360 / 30)];
}

function declination(v: { x: number; y: number; z: number }): number {
  const d = Math.sqrt(v.x ** 2 + v.y ** 2 + v.z ** 2);
  return Math.asin(v.z / d);
}

// ── Moon zodiac sign (sidereal) ───────────────────────────────────────────────
export function getMoonZodiacSign(date: Date): ZodiacSign {
  const ecl = Ecliptic(GeoVector(Body.Moon, date, false));
  return lonToSign(tropicalToSidereal(ecl.elon, date));
}

export function getMoonSignDegrees(date: Date): number {
  const ecl = Ecliptic(GeoVector(Body.Moon, date, false));
  return Math.round(tropicalToSidereal(ecl.elon, date) % 30);
}

// ── Moon phase ────────────────────────────────────────────────────────────────
export function getMoonPhaseData(date: Date): {
  phase: MoonPhase;
  angle: number;
  illumination: number;
} {
  const angle = AstroMoonPhase(date);
  const illumination = Math.round(((1 - Math.cos((angle * Math.PI) / 180)) / 2) * 100);

  let phase: MoonPhase;
  if      (angle < 6.25 || angle >= 353.75) phase = 'new';
  else if (angle < 83.25)                    phase = 'waxing-crescent';
  else if (angle < 96.25)                    phase = 'first-quarter';
  else if (angle < 173.25)                   phase = 'waxing-gibbous';
  else if (angle < 186.75)                   phase = 'full';
  else if (angle < 263.75)                   phase = 'waning-gibbous';
  else if (angle < 276.75)                   phase = 'last-quarter';
  else                                        phase = 'waning-crescent';

  return { phase, angle, illumination };
}

// ── Moon ascending / descending ───────────────────────────────────────────────
// Moon is ascending when its declination is increasing (rising higher in sky).
// Biodynamic calendar: ascending = sow & plant; descending = transplant & prune.
export function isMoonAscending(date: Date): boolean {
  const t = date.getTime();
  const v1 = GeoVector(Body.Moon, new Date(t - 12 * 3_600_000), false);
  const v2 = GeoVector(Body.Moon, new Date(t + 12 * 3_600_000), false);
  return declination(v2) > declination(v1);
}

// ── Lunar node days in a calendar month ───────────────────────────────────────
// Lunar nodes = Moon crosses the ecliptic. Both ascending (☊) and descending (☋)
// nodes are considered disruptive for sowing — avoid 3 h either side.
export interface NodeDay {
  day: number;
  kind: 'ascending' | 'descending';
}

export function getMonthNodeDays(year: number, month: number): NodeDay[] {
  const start = new Date(year, month, 1);
  const end   = new Date(year, month + 1, 0, 23, 59, 59);
  const results: NodeDay[] = [];

  let node = SearchMoonNode(new Date(start.getTime() - 14 * 86_400_000));

  while (node.time.date <= end) {
    if (node.time.date >= start) {
      results.push({
        day:  node.time.date.getDate(),
        kind: node.kind === NodeEventKind.Ascending ? 'ascending' : 'descending',
      });
    }
    node = NextMoonNode(node);
  }

  return results;
}

// ── Perigee / Apogee days in a calendar month ────────────────────────────────
// Perigee (Pg) — Moon closest to Earth. Seeds may struggle or lie dormant.
// Apogee  (Ag) — Moon furthest from Earth. Plants may bolt or flower early.
export interface ApsisDay {
  day: number;
  type: 'perigee' | 'apogee';
}

export function getMonthApsides(year: number, month: number): ApsisDay[] {
  const start = new Date(year, month, 1);
  const end   = new Date(year, month + 1, 0, 23, 59, 59);
  const results: ApsisDay[] = [];

  let apsis = SearchLunarApsis(new Date(start.getTime() - 15 * 86_400_000));

  while (apsis.time.date <= end) {
    if (apsis.time.date >= start) {
      results.push({
        day:  apsis.time.date.getDate(),
        type: apsis.kind === ApsisKind.Pericenter ? 'perigee' : 'apogee',
      });
    }
    apsis = NextLunarApsis(apsis);
  }

  return results;
}

// ── Moon quarter events in a calendar month ───────────────────────────────────
// The exact instant of each new / first quarter / full / last quarter moon.
// Classifying a day by its phase angle at midday is good enough for colouring a
// calendar, but it can land a day either side of the true event — so anything
// that states a date outright should use these instead.
export interface MoonQuarterEvent {
  day: number;
  date: Date;
  quarter: 'new' | 'first-quarter' | 'full' | 'last-quarter';
}

const QUARTER_NAMES: MoonQuarterEvent['quarter'][] = ['new', 'first-quarter', 'full', 'last-quarter'];

export function getMonthMoonQuarters(year: number, month: number): MoonQuarterEvent[] {
  const start = new Date(year, month, 1);
  const end   = new Date(year, month + 1, 0, 23, 59, 59);
  const results: MoonQuarterEvent[] = [];

  let q = SearchMoonQuarter(new Date(start.getTime() - 10 * 86_400_000));

  while (q.time.date <= end) {
    if (q.time.date >= start) {
      results.push({
        day:     q.time.date.getDate(),
        date:    q.time.date,
        quarter: QUARTER_NAMES[q.quarter],
      });
    }
    q = NextMoonQuarter(q);
  }

  return results;
}

// ── Planetary positions (sidereal zodiac) ─────────────────────────────────────
// Shows where the major planets sit in the zodiac today.
// Per Maria Thun: the planets carry their element's influence through the sign
// they occupy, amplifying that element's biodynamic effect.
export type PlanetElement = 'fire' | 'earth' | 'air' | 'water';

export interface PlanetaryPosition {
  planet:        string;
  symbol:        string;
  zodiacSign:    ZodiacSign;
  zodiacSymbol:  string;
  element:       PlanetElement;
  dayTypeAffinity: BiodynamicDayType;
}

const ELEMENT_DAY_TYPE: Record<PlanetElement, BiodynamicDayType> = {
  fire: 'fruit', earth: 'root', air: 'flower', water: 'leaf',
};

const PLANETS: Array<{ body: Body; name: string; symbol: string }> = [
  { body: Body.Saturn,  name: 'Saturn',  symbol: '♄' },
  { body: Body.Jupiter, name: 'Jupiter', symbol: '♃' },
  { body: Body.Mars,    name: 'Mars',    symbol: '♂' },
  { body: Body.Venus,   name: 'Venus',   symbol: '♀' },
  { body: Body.Mercury, name: 'Mercury', symbol: '☿' },
];

export function getPlanetaryPositions(date: Date): PlanetaryPosition[] {
  return PLANETS.flatMap(({ body, name, symbol }) => {
    try {
      const ecl     = Ecliptic(GeoVector(body, date, true));
      const sign    = lonToSign(tropicalToSidereal(ecl.elon, date));
      const element = ZODIAC_ELEMENT[sign];
      return [{
        planet: name, symbol,
        zodiacSign: sign, zodiacSymbol: ZODIAC_SYMBOLS[sign],
        element, dayTypeAffinity: ELEMENT_DAY_TYPE[element],
      }];
    } catch { return []; }
  });
}

// ── Full day summary (used for today panel) ───────────────────────────────────
export interface DayAstronomy {
  moonZodiacSign:    ZodiacSign;
  moonZodiacSymbol:  string;
  moonSignDegrees:   number;
  moonDayType:       BiodynamicDayType;
  moonPhase:         MoonPhase;
  moonPhaseAngle:    number;
  moonIllumination:  number;
  moonAscending:     boolean;
  nearPerigee:       boolean;
  nearApogee:        boolean;
  nearNode:          boolean;
  nodeKind:          'ascending' | 'descending' | null;
  avoidPlanting:     boolean;
  planetaryPositions: PlanetaryPosition[];
}

export function getDayAstronomy(date: Date): DayAstronomy {
  const sign = getMoonZodiacSign(date);
  const { phase, angle, illumination } = getMoonPhaseData(date);

  const nodeDays  = getMonthNodeDays(date.getFullYear(), date.getMonth());
  const apsidDays = getMonthApsides(date.getFullYear(), date.getMonth());
  const nodeDay   = nodeDays.find(n => n.day === date.getDate());
  const apsisDay  = apsidDays.find(a => a.day === date.getDate());

  const nearNode    = !!nodeDay;
  const nearPerigee = apsisDay?.type === 'perigee';
  const nearApogee  = apsisDay?.type === 'apogee';

  return {
    moonZodiacSign:    sign,
    moonZodiacSymbol:  ZODIAC_SYMBOLS[sign],
    moonSignDegrees:   getMoonSignDegrees(date),
    moonDayType:       ZODIAC_TO_DAY_TYPE[sign],
    moonPhase:         phase,
    moonPhaseAngle:    angle,
    moonIllumination:  illumination,
    moonAscending:     isMoonAscending(date),
    nearPerigee,
    nearApogee,
    nearNode,
    nodeKind:          nodeDay?.kind ?? null,
    avoidPlanting:     nearNode || nearPerigee,
    planetaryPositions: getPlanetaryPositions(date),
  };
}
